import type { VehicleRarity } from '../api/types'
import { formatNumber, formatPercent } from '../domain/format'
import { colourShare, summariseColours, summariseFacts } from '../domain/rarity'
import { Icon } from './Icon'

/**
 * How many cars like this one are registered in the Netherlands, and in which colours.
 *
 * The one figure here that needs care is what a count actually means: these are vehicles *currently
 * registered*, not vehicles ever sold. A model that sold well in 2005 and has mostly been scrapped
 * looks rare by this measure without ever having been rare. The note at the bottom says so, for the
 * same reason the review section says it reviews the model rather than the car.
 *
 * Renders nothing when the API returned the envelope without figures, which happens for a vehicle
 * whose registration carries no make or model — there is nothing to count against, and an empty
 * card claiming rarity would be worse than no card.
 */
export function RaritySection({ rarity }: { rarity: VehicleRarity }) {
  const total = rarity.registeredInNetherlands

  if (total === undefined) {
    return null
  }

  const colours = summariseColours(rarity)
  const share = colourShare(rarity)
  const facts = summariseFacts(rarity.didYouKnow)
  const model = [rarity.brand, rarity.model].filter(Boolean).join(' ')

  // The bars are scaled against the largest row, which is not necessarily the first: the summed
  // tail can outweigh any single colour on a model with no dominant one.
  const widest = Math.max(...colours.map((colour) => colour.sharePercent), 1)

  return (
    <div className="rarity">
      {/* One paragraph per sentence: two claims, two lines. In one block the second sentence starts
          wherever the first happens to end, which reads as a wrapping accident. */}
      <div className="rarity__intro">
        <p>
          Er {total === 1 ? 'is' : 'zijn'} <strong>{formatNumber(total)}</strong>{' '}
          {total === 1 ? 'auto' : "auto's"} van dit model{model ? ` (${model})` : ''} in Nederland
          geregistreerd.
        </p>

        {rarity.registeredInThisColour !== undefined && rarity.colour && (
          <p>
            Daarvan {rarity.registeredInThisColour === 1 ? 'is er' : 'zijn er'}{' '}
            <strong>{formatNumber(rarity.registeredInThisColour)}</strong>
            {share !== undefined && ` (${formatPercent(share)})`} net als deze auto{' '}
            {rarity.colour.toLowerCase()}.
          </p>
        )}
      </div>

      {colours.length > 0 && (
        <ul className="rarity__colours">
          {colours.map((colour) => (
            <li
              className={`rarity__colour${colour.isThisCar ? ' rarity__colour--this' : ''}${
                colour.isOther ? ' rarity__colour--other' : ''
              }`}
              key={colour.label}
            >
              <span className="rarity__colour-name">
                {colour.label}
                {colour.isThisCar && <span className="rarity__badge">deze auto</span>}
              </span>
              <span className="rarity__bar" aria-hidden="true">
                {/* Scaled against the widest row rather than against 100%, so a model that is 80%
                    grey does not flatten every other colour into an invisible sliver. */}
                <span
                  className="rarity__bar-fill"
                  style={{ width: `${Math.max((colour.sharePercent / widest) * 100, 1.5)}%` }}
                />
              </span>
              <span className="rarity__colour-count">
                {formatNumber(colour.count)}
                <span className="rarity__colour-share">{formatPercent(colour.sharePercent)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {facts.length > 0 && <DidYouKnow facts={facts} />}

      {/* Kept short enough to sit on one line at the card's full width. */}
      <p className="card__footnote">
        Het gaat om nu geregistreerde voertuigen, niet om hoeveel er ooit zijn verkocht. Een vaak
        gesloopt model lijkt zo zeldzamer dan het was.
      </p>
    </div>
  )
}

/**
 * The "wist je dat" corner.
 *
 * Small on purpose, and often empty. The API sends a fact only when it is worth reading and this
 * component drops the two kinds the card above has already stated in its opening sentence — see
 * `summariseFacts` — so an ordinary car in an ordinary colour gets one line about its plate, or
 * nothing at all. That restraint is the feature: a section padded with the unremarkable is one
 * people learn to scroll past, and this is meant to be the part somebody screenshots.
 */
function DidYouKnow({ facts }: { facts: string[] }) {
  return (
    <aside className="did-you-know">
      <h3 className="did-you-know__title">
        <Icon name="info" size={16} />
        Wist je dat?
      </h3>
      <ul className="did-you-know__list">
        {facts.map((fact) => (
          <li key={fact}>{fact}</li>
        ))}
      </ul>
    </aside>
  )
}
